import type { ShopifyOrderRow } from "@staff-app/shared-types";
import { ExternalLink } from "lucide-react";
import { CopyButton, trackingClipboard } from "@/components/CopyButton";
import { formatMoney } from "@/lib/money";
import {
  DATA_TABLE_CELLS,
  DATA_TABLE_HEADER,
  DATA_TABLE_ROW,
  DATA_TABLE_WRAPPER,
} from "@/lib/data-table";
import { StatusBadge } from "./OrderStatusBadge";

/**
 * Date only — no time.
 *
 * "Aug 18, 2026, 3:17 PM" was the widest fixed cell in the table and a large part of why the row
 * overflowed sideways. Scanning a list only ever needs the day; the exact timestamp is on the
 * order card, where there is room to state it in full.
 */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function OrderTable({
  rows,
  loading,
  onOpen,
}: {
  rows: ShopifyOrderRow[];
  loading: boolean;
  onOpen: (row: ShopifyOrderRow) => void;
}) {
  if (!rows.length) {
    return (
      <div
        className="rounded-md border p-8 text-center text-sm text-muted-foreground"
        data-testid="orders-empty"
      >
        {loading ? "Loading orders…" : "No orders match this search."}
      </div>
    );
  }

  return (
    <div className={DATA_TABLE_WRAPPER}>
      <table
        className={`w-full text-sm ${DATA_TABLE_CELLS}`}
        data-testid="orders-table"
      >
        <thead className={DATA_TABLE_HEADER}>
          <tr>
            <th>Order</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Total</th>
            <th>Channel</th>
            <th>Fulfillment</th>
            <th>Tracking</th>
            <th>Payment</th>
            <th>Delivery</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className={`cursor-pointer ${DATA_TABLE_ROW}`}
              onClick={() => onOpen(r)}
              data-testid={`order-row-${r.name}`}
            >
              <td className="whitespace-nowrap font-medium">{r.name}</td>
              <td className="whitespace-nowrap text-muted-foreground">
                {when(r.createdAt)}
              </td>
              <td className="max-w-[15rem]">
                {/* Email leads: it is the order's own contact address and what staff search by.
                    The name underneath is the SHIP-TO recipient, labelled as such — on a business
                    order it is not the account holder (Airtech Intl ships to "Jesse Rios"). */}
                <div className="truncate">{r.customerEmail ?? "—"}</div>
                {r.shipToName && (
                  <div className="truncate text-xs text-muted-foreground">
                    to {r.shipToName}
                  </div>
                )}
              </td>
              <td>{r.itemCount}</td>
              <td className="whitespace-nowrap">
                {formatMoney(r.totalAmount, r.totalCurrency)}
              </td>
              <td
                className="max-w-[9rem] truncate text-muted-foreground"
                title={r.channel ?? undefined}
              >
                {r.channel ?? "—"}
              </td>
              <td>
                <StatusBadge
                  status={r.displayFulfillmentStatus}
                  kind="fulfillment"
                />
              </td>
              <td
                className="whitespace-nowrap px-2.5 py-2"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Only shipped orders have a number. An unfulfilled row shows a dash rather than
                    an empty cell, so "not shipped yet" is stated rather than merely absent. */}
                {r.trackingNumber ? (
                  <span className="inline-flex items-center">
                    {r.trackingUrl ? (
                      <a
                        href={r.trackingUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex max-w-[8.5rem] items-center gap-1 truncate font-mono text-xs text-primary hover:underline"
                        title={r.trackingNumber}
                      >
                        {r.trackingNumber}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      // Shopify sometimes has a number but no carrier URL — still worth copying.
                      <span
                        className="max-w-[8.5rem] truncate font-mono text-xs"
                        title={r.trackingNumber}
                      >
                        {r.trackingNumber}
                      </span>
                    )}
                    <CopyButton
                      {...trackingClipboard(r.trackingNumber, r.trackingUrl)}
                      label="Tracking number"
                      testId={`copy-tracking-${r.name}`}
                      stopPropagation
                    />
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td>
                <StatusBadge
                  status={r.displayFinancialStatus}
                  kind="financial"
                />
              </td>
              <td
                className="max-w-[9rem] truncate text-muted-foreground"
                title={r.deliveryMethod ?? undefined}
              >
                {r.deliveryMethod ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
